import { RubyGemMark } from '../components/RubyGemMark';
import { SOCIAL_PUBLISHING_ENABLED } from '../config/socialPublishing';
import { RubyLoader } from '../components/RubyLoader';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, Building2, Shield, Clock, Globe,
  Package, MapPin, Store, Warehouse, Tent,
  CheckCircle, MessageSquare, XCircle, CreditCard
} from 'lucide-react';
import { api } from '../services/apiService';
import AdminDetailsTab from '../components/locations/AdminDetailsTab';
import AdminRegistrationsTab from '../components/locations/AdminRegistrationsTab';
import AdminHoursTab from '../components/locations/AdminHoursTab';
import AdminOnlinePresenceTab from '../components/locations/AdminOnlinePresenceTab';
import AdminAssetsTab from '../components/locations/AdminAssetsTab';
import AdminLicensesTab from '../components/locations/AdminLicensesTab';
import ApproveLocationForm from '../components/locations/ApproveLocationForm';

type TabId = 'details' | 'registrations' | 'hours' | 'online' | 'billing' | 'assets' | 'licenses';

// BILLING MOVED (2026-08-26 Plan & Packages spec): location billing lives on
// the tenant's Plan & Packages tab now — one billing surface, no duplicate.
// 'billing' stays a VALID tab id so bookmarked ?tab=billing URLs land on the
// pointer card below instead of silently falling back to Details, but the
// tab button itself is gone from the bar.
const ALL_TABS: Array<{ id: TabId; label: string; icon: React.FC<any> }> = [
  { id: 'details', label: 'Details', icon: MapPin },
  { id: 'registrations', label: 'Registrations', icon: Building2 },
  { id: 'hours', label: 'Hours', icon: Clock },
  ...(SOCIAL_PUBLISHING_ENABLED ? [{ id: 'online' as TabId, label: 'Online Presence', icon: Globe }] : []),
  { id: 'assets', label: 'Assets', icon: Package },
  { id: 'licenses', label: 'Licenses', icon: Shield },
];

// Kept for the tab-bar filter below (now a no-op since the Billing button is
// gone) — see the BILLING MOVED note above.
const BILLING_HIDDEN_STATUSES = new Set(['pending', 'info_requested', 'rejected']);

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    payment_pending: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    deactivation_requested: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    grace: 'bg-red-500/20 text-red-400 border-red-500/30',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
    inactive: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${map[status] || map.inactive}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
};

const typeIcon = (type: string) => {
  switch (type) {
    case 'store': return <Store size={18} className="text-white/70" />;
    case 'warehouse': return <Warehouse size={18} className="text-white/70" />;
    case 'popup': return <Tent size={18} className="text-white/70" />;
    default: return <MapPin size={18} className="text-white/70" />;
  }
};

const VALID_TABS: TabId[] = ['details', 'registrations', 'hours', ...(SOCIAL_PUBLISHING_ENABLED ? ['online' as TabId] : []), 'billing', 'assets', 'licenses'];

const SuperAdminLocationDetail: React.FC = () => {
  const { tenantId, requestId } = useParams<{ tenantId: string; requestId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [request, setRequest] = useState<any>(null);
  const [location, setLocation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Decision modal state
  type DecisionType = 'approve' | 'reject' | 'info';
  const [decisionType, setDecisionType] = useState<DecisionType | null>(null);
  const [decisionInput, setDecisionInput] = useState('');
  const [decisionSaving, setDecisionSaving] = useState(false);

  const openDecision = (type: DecisionType) => {
    setDecisionType(type);
    setDecisionInput('');
  };

  const submitDecision = async () => {
    if (!decisionType || !request || !tenantId) return;
    try {
      setDecisionSaving(true);
      if (decisionType === 'reject') {
        if (!decisionInput.trim()) return;
        await api.locationBilling.rejectRequest(request.requestId, { tenantId, reason: decisionInput.trim() });
      } else if (decisionType === 'info') {
        if (!decisionInput.trim()) return;
        await api.locationBilling.requestInfo(request.requestId, { tenantId, message: decisionInput.trim() });
      }
      setDecisionType(null);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Action failed');
    } finally {
      setDecisionSaving(false);
    }
  };

  const tabParam = searchParams.get('tab') as TabId | null;
  const activeTab: TabId = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'details';
  const setActiveTab = (id: TabId) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', id);
      return next;
    }, { replace: true });
  };

  const fetchData = useCallback(async () => {
    if (!tenantId || !requestId) return;
    try {
      setLoading(true);
      setError(null);

      // Fetch all location billing requests for this tenant, find the matching one
      const data: any = await api.locationBilling.listTenantRequests(tenantId);
      const requests = Array.isArray(data) ? data : data?.items || data?.requests || [];
      const found = requests.find((r: any) => r.requestId === requestId);

      if (!found) {
        setError('Location request not found');
        setLoading(false);
        return;
      }
      setRequest(found);

      // Fetch full location data (location + assets + licenses) via admin endpoint
      if (found.locationId) {
        try {
          const fullData: any = await api.locations.getFullLocation(requestId, tenantId);
          if (fullData?.location) {
            setLocation({ ...fullData.location, _assets: fullData.assets || [], _licenses: fullData.licenses || [] });
          }
        } catch (locErr: any) {
          console.warn('Could not fetch location details:', locErr.message);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch location data:', err);
      setError(err.message || 'Failed to load location data');
    } finally {
      setLoading(false);
    }
  }, [tenantId, requestId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <RubyLoader variant="page" className="min-h-[60vh]" />
    );
  }

  if (error || !request) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate(`/tenant/${tenantId}?tab=locations`)}
          className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-700 text-sm mb-4"
        >
          <ChevronLeft size={16} />
          Back to Tenant
        </button>
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-8 text-center">
          <MapPin size={32} className="mx-auto mb-3 text-stone-300" />
          <p className="text-stone-500">{error || 'Location not found'}</p>
        </div>
      </div>
    );
  }

  const data = location || request;
  const addr = data?.address;

  return (
    <div className="min-h-screen bg-stone-100 -m-3 md:-m-6">
      {/* Header */}
      <div className="bg-black">
        <div className="px-4 pt-4 pb-0">
          {/* Back button */}
          <button
            onClick={() => navigate(`/tenant/${tenantId}?tab=locations`)}
            className="inline-flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-3 transition-colors"
          >
            <ChevronLeft size={16} />
            Back to Tenant
          </button>

          {/* Name + Status */}
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2.5 rounded-xl bg-gray-800 border border-gray-700 shrink-0">
              {typeIcon(data?.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-white text-lg font-bold truncate">{data?.name || 'Location'}</h1>
                {statusBadge(request.status)}
              </div>
              {addr && (
                <p className="text-gray-400 text-sm mt-0.5 truncate">
                  {addr.street}, {addr.city}, {addr.state} {addr.zip}
                </p>
              )}
            </div>
            {request.priceAmount && (
              <div className="text-right shrink-0">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Monthly</p>
                <p className={`text-sm font-semibold ${request.isFree ? 'text-emerald-400' : 'text-white'}`}>
                  {request.isFree ? 'Free' : `$${(request.priceAmount / 100).toFixed(2)}`}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className="px-4 pb-3 pt-3">
          <div className="flex gap-2 flex-wrap">
            {ALL_TABS
              .filter(tab => tab.id !== 'billing' || !BILLING_HIDDEN_STATUSES.has(request?.status))
              .map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors min-h-[36px] ${
                    isActive
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Decision action bar — only for pending / info_requested */}
      {(request.status === 'pending' || request.status === 'info_requested') && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="px-4 md:px-6 lg:px-8 py-3 w-full max-w-[90%] mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-amber-900">
              <span className="font-medium">Review this request</span>
              <span className="text-amber-700">— check all tabs before deciding.</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => openDecision('approve')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 min-h-[36px]"
              >
                <CheckCircle size={14} /> Approve
              </button>
              <button
                onClick={() => openDecision('info')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 min-h-[36px]"
              >
                <MessageSquare size={14} /> Request Info
              </button>
              <button
                onClick={() => openDecision('reject')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-red-600 hover:bg-red-700 min-h-[36px]"
              >
                <XCircle size={14} /> Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decision modal */}
      {decisionType && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !decisionSaving && setDecisionType(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-stone-900 font-semibold text-lg mb-1">
              {decisionType === 'approve' && 'Approve Location Request'}
              {decisionType === 'reject' && 'Reject Location Request'}
              {decisionType === 'info' && 'Request More Information'}
            </h3>
            <p className="text-stone-500 text-sm mb-4">
              {request?.name}
            </p>
            {decisionType === 'approve' && tenantId && request && (
              <ApproveLocationForm
                tenantId={tenantId}
                requestId={request.requestId}
                locationName={request.name}
                onDone={async () => { setDecisionType(null); await fetchData(); }}
                onCancel={() => setDecisionType(null)}
              />
            )}
            {(decisionType === 'reject' || decisionType === 'info') && (
              <>
                <textarea
                  value={decisionInput}
                  onChange={(e) => setDecisionInput(e.target.value)}
                  placeholder={decisionType === 'reject' ? 'Reason for rejection' : 'What information do you need?'}
                  rows={4}
                  className="w-full bg-white border border-stone-300 rounded-lg px-3 py-2 text-stone-900 text-sm mb-4"
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setDecisionType(null)}
                    disabled={decisionSaving}
                    className="px-4 py-2 text-sm text-stone-600 hover:text-stone-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitDecision}
                    disabled={decisionSaving || !decisionInput.trim()}
                    className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-2 ${
                      decisionType === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {decisionSaving && <RubyGemMark size={14} drawing color="currentColor" />}
                    {decisionType === 'reject' ? 'Reject' : 'Send Request'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4 md:p-6 lg:p-8 w-full max-w-[90%] mx-auto">
        {activeTab === 'details' && (
          <AdminDetailsTab location={location} request={request} />
        )}
        {activeTab === 'registrations' && (
          <AdminRegistrationsTab
            location={location}
            request={request}
            tenantId={tenantId!}
            onRefresh={handleRefresh}
          />
        )}
        {activeTab === 'hours' && (
          <AdminHoursTab location={location || request} />
        )}
        {activeTab === 'online' && (
          <AdminOnlinePresenceTab location={location || request} tenantId={tenantId} requestId={requestId} locationId={request.locationId || null} />
        )}
        {activeTab === 'billing' && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-8 text-center">
            <CreditCard size={36} className="mx-auto mb-3 text-stone-300" />
            <h3 className="text-stone-800 font-semibold mb-1">Billing has moved</h3>
            <p className="text-sm text-stone-500 mb-4 max-w-md mx-auto">
              This location's billing — price, discounts, comp/free, deactivation and cancellation approvals —
              is managed on the tenant's <span className="font-medium">Plan &amp; Packages</span> tab, alongside
              the software packages and the rest of the monthly bill.
            </p>
            <button
              onClick={() => navigate(`/tenant/${tenantId}?tab=packages`)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium"
            >
              Open Plan &amp; Packages
            </button>
          </div>
        )}
        {activeTab === 'assets' && (
          <AdminAssetsTab assets={location?._assets || []} />
        )}
        {activeTab === 'licenses' && (
          <AdminLicensesTab licenses={location?._licenses || []} />
        )}
      </div>
    </div>
  );
};

export default SuperAdminLocationDetail;
